import { useEffect, useState } from 'react';
import useIsDanishIntermediary from '../../hooks/useIsDanishIntermediary';
import { useFlag } from '../../hooks/useFlag';
import { useCountries } from '../../hooks/useCountries';
import { useLearningMarkets } from '../../hooks/useLearningMarkets';
import { getUsersServices, userIsUserAdminForAnyPartner } from '../../utils/services';
import { getServiceUrl } from '../../utils/environment';
import { csrdRestoreLastSubpageState, csrdRoutePaths } from '../../routePaths/csrdRoutePaths';
import { MenuLinksWrapper, StyledLink } from './styles';
import type { TextType } from '../types';
import type { BasicProfile } from '../../Profile';
import type { MenuService } from '../../Services/useServices';

type MenuLinksProps = {
  profile: BasicProfile | null;
  allServices?: MenuService[];
  activeLink?: string;
  text: TextType;
  serviceNames?: Record<string, string>;
};

const MenuLinks = ({
  profile,
  allServices = [],
  activeLink,
  text,
  serviceNames,
}: MenuLinksProps) => {
  const [userServices, setUserServices] = useState<MenuService[]>();

  const hasFollowUpService = profile?.aggregatedPermissions.services.followUp;

  const hideAdmin = useFlag('hide-admin-in-menu');

  const environmentFlag = useFlag('environment-service');
  const csrdFlag = useFlag('csrd');
  const csrdAllowDenmark = useFlag('csrd-allow-denmark');
  const csrdAllowFinland = useFlag('csrd-allow-finland');
  const csrdAllowPoland = useFlag('csrd-allow-poland');
  const csrdAllowSweden = useFlag('csrd-allow-sweden');
  const csrdAllowNorway = useFlag('csrd-allow-norway');

  const showLearning = useFlag('app-learning');
  const { hasAvailableMarket: hasLearningMarket } = useLearningMarkets();
  const co2NorwayFlag = useFlag('co2-impact-norway');

  const isDanishIntermediary = useIsDanishIntermediary();
  const { supportedCountries } = useCountries();

  const showCSRD =
    csrdFlag &&
    hasFollowUpService &&
    ((supportedCountries.sweden && csrdAllowSweden) ||
      (supportedCountries.denmark && csrdAllowDenmark) ||
      (supportedCountries.finland && csrdAllowFinland) ||
      (supportedCountries.norway && csrdAllowNorway) ||
      (supportedCountries.poland && csrdAllowPoland));

  useEffect(() => {
    if (allServices.length && profile) {
      setUserServices(getUsersServices(profile, allServices));
    }
  }, [profile, allServices]);

  const showCO2Impact =
    hasFollowUpService &&
    !isDanishIntermediary &&
    (supportedCountries.sweden ||
      supportedCountries.denmark ||
      (co2NorwayFlag && supportedCountries.norway));

  const showEnvironment = environmentFlag && hasFollowUpService;

  const userShouldSeeAdmin = userIsUserAdminForAnyPartner(profile);

  return (
    <MenuLinksWrapper>
      {userServices?.map((userService) => (
        <ServiceLink
          key={userService.id}
          activeLink={activeLink}
          userService={userService}
          serviceNames={serviceNames}
        />
      ))}

      <>
        {showCO2Impact && <CO2Link text={text} activeLink={activeLink} />}
        {showEnvironment && <EnvironmentLink text={text} activeLink={activeLink} />}
        {showCSRD && <CSRDLink text={text} activeLink={activeLink} />}
      </>

      {showLearning && hasLearningMarket && <LearningLink text={text} activeLink={activeLink} />}

      {userShouldSeeAdmin && !hideAdmin && <UserAdminLink text={text} activeLink={activeLink} />}
    </MenuLinksWrapper>
  );
};

const ServiceLink = ({
  activeLink,
  userService,
  serviceNames,
}: {
  activeLink?: string;
  userService: MenuService;
  serviceNames?: Record<string, string>;
}) => {
  return (
    <StyledLink
      key={userService.id}
      to={getServiceUrl(userService)}
      isActive={userService.name === activeLink}
      data-testid={userService.linkText}
    >
      {serviceNames?.[userService.name]}
    </StyledLink>
  );
};

const CO2Link = ({ text, activeLink }: { text: TextType; activeLink?: string }) => {
  return (
    <StyledLink to="/co2impact" data-testid="co2_impact" isActive={'co2impact' === activeLink}>
      {text.co2Impact}
    </StyledLink>
  );
};

const CSRDLink = ({ text, activeLink }: { text: TextType; activeLink?: string }) => {
  return (
    <StyledLink
      to={csrdRoutePaths.HOME}
      state={csrdRestoreLastSubpageState}
      data-testid="csrd"
      isActive={'csrd' === activeLink}
    >
      {text.csrd}
    </StyledLink>
  );
};

const EnvironmentLink = ({ text, activeLink }: { text: TextType; activeLink?: string }) => {
  return (
    <StyledLink to="/environment" data-testid="environment" isActive={'environment' === activeLink}>
      {text.environment}
    </StyledLink>
  );
};

const LearningLink = ({ text, activeLink }: { text: TextType; activeLink?: string }) => {
  return (
    <StyledLink to="/learning" data-testid="learning-link" isActive={activeLink === 'learning'}>
      {text.learning}
    </StyledLink>
  );
};

const UserAdminLink = ({ text, activeLink }: { text: TextType; activeLink?: string }) => {
  return (
    <StyledLink to="/admin" data-testid="admin" isActive={'usermanagement' === activeLink}>
      {text.userManagement}
    </StyledLink>
  );
};

export default MenuLinks;
